// core/action/supplierActions.js
import { supplierAPI } from './api';

export const supplierActions = {
    // Get all suppliers
    getSuppliers: async (params = {}) => {
        try {
            console.log('Fetching suppliers with params:', params);
            
            const response = await supplierAPI.getAll(params);
            if (response && response.success) {
                return { 
                    success: true, 
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || 1
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to fetch suppliers',
                    data: [],
                    total: 0
                };
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            return { 
                success: false, 
                message: error.message || 'Failed to fetch suppliers',
                data: [],
                total: 0
            };
        }
    },

    // Get single supplier by ID
    getSupplier: async (id) => {
        try {
            const response = await supplierAPI.get(id);
            if (response && response.success) {
                return { 
                    success: true, 
                    data: response.data
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Supplier not found' 
                };
            }
        } catch (error) {
            console.error('Error fetching supplier:', error);
            return { 
                success: false, 
                message: error.message || 'Failed to fetch supplier' 
            };
        }
    },

    // Create new supplier
    createSupplier: async (supplierData) => {
        try {
            // Validate required fields
            if (!supplierData.SupplierName || supplierData.SupplierName.trim() === '') {
                return { success: false, message: 'Supplier name is required' };
            }
            
            const response = await supplierAPI.create(supplierData);
            
            if (response && response.success) {
                return { 
                    success: true, 
                    data: response.data,
                    message: response.message || 'Supplier created successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to create supplier' 
                };
            }
        } catch (error) {
            console.error('Error creating supplier:', error);
            return { 
                success: false, 
                message: error.message || 'Failed to create supplier' 
            };
        }
    },

    // Update supplier - FIXED: Pass data directly (id should be inside)
    updateSupplier: async (id, supplierData) => {
        try {
            if (!id) {
                return { success: false, message: 'Supplier ID is required' };
            }
            
            // Create a clean data object with id at top level
            const updateData = {
                id: id,  // id at top level, not nested
                SupplierName: supplierData.SupplierName,
                SupplierCode: supplierData.SupplierCode,
                SupplierSlug: supplierData.SupplierSlug,
                Email: supplierData.Email,
                Phone: supplierData.Phone,
                Address: supplierData.Address,
                GSTNo: supplierData.GSTNo,
                ContactPerson: supplierData.ContactPerson,
                Designation: supplierData.Designation,
                Website: supplierData.Website,
                Notes: supplierData.Notes,
                Country: supplierData.Country,
                State: supplierData.State,
                City: supplierData.City
            };
            
            console.log('Sending update data:', updateData); // Debug log
            
            const response = await supplierAPI.update(updateData);
            
            if (response && response.success) {
                return { 
                    success: true, 
                    data: response.data,
                    message: response.message || 'Supplier updated successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to update supplier' 
                };
            }
        } catch (error) {
            console.error('Error updating supplier:', error);
            return { 
                success: false, 
                message: error.message || 'Failed to update supplier' 
            };
        }
    },

    // Delete supplier
    deleteSupplier: async (deleteData) => {
        try {
            if (!deleteData) {
                return { success: false, message: 'Supplier ID is required' };
            }
            
            const id = typeof deleteData === 'object' ? deleteData.id : deleteData;
            const data = { id };
            
            const response = await supplierAPI.delete(data);
            
            if (response && response.success) {
                return { 
                    success: true, 
                    message: response.message || 'Supplier deleted successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to delete supplier' 
                };
            }
        } catch (error) {
            console.error('Error deleting supplier:', error);
            return { 
                success: false, 
                message: error.message || 'Failed to delete supplier' 
            };
        }
    },

    // Check if supplier code exists
    checkSupplierCode: async (code) => {
        try {
            if (!code) return false;
            
            const response = await supplierAPI.checkCode({ code });
            
            if (response && response.exists !== undefined) {
                return response.exists;
            }
            
            return false;
        } catch (error) {
            console.error('Error checking supplier code:', error);
            return false;
        }
    },

    // Search suppliers
    searchSuppliers: async (searchTerm) => {
        try {
            if (!searchTerm || searchTerm.length < 2) {
                return { success: true, data: [] };
            }
            
            const response = await supplierAPI.search({ search: searchTerm, limit: 20 });
            
            if (response && response.success && response.data) {
                const formattedSuppliers = response.data.map(supplier => ({
                    value: supplier.id,
                    label: `${supplier.name} (${supplier.phone || 'No phone'})`,
                    phone: supplier.phone,
                    email: supplier.email,
                    address: supplier.address,
                    ...supplier
                }));
                
                return { success: true, data: formattedSuppliers };
            }
            
            return { success: true, data: [] };
        } catch (error) {
            console.error('Error searching suppliers:', error);
            return { success: true, data: [] };
        }
    },

    // Get supplier options for dropdown
    getSupplierOptions: async () => {
        try {
            const result = await supplierActions.getSuppliers({ limit: 1000 });
            
            if (result.success) {
                const options = [
                    { value: '', label: 'All Suppliers' },
                    ...result.data.map(s => ({
                        value: s.id,
                        label: `${s.name} (${s.phone || 'No phone'})`
                    }))
                ];
                
                return { success: true, data: options };
            }
            
            return { success: false, data: [{ value: '', label: 'All Suppliers' }] };
        } catch (error) {
            console.error('Error getting supplier options:', error);
            return { success: false, data: [{ value: '', label: 'All Suppliers' }] };
        }
    }
};

export default supplierActions;
